import { Box, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractChip } from './ContractChip';
import { usePlanEditor } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The floating bulk bar (visible while rows are selected): status, contract,
 * outdent/indent, duplicate, delete, clear. Task-only fields skip selected
 * top-level rows (BR-2).
 *
 * Staffing is deliberately absent — see the note where "Assign" used to be.
 */

const BarButton = ({
  icon,
  label,
  title,
  color,
  automationId,
  onClick,
}: {
  icon: string;
  label?: string;
  /** Tooltip for an icon-only button (one without a `label`). */
  title?: string;
  color?: string;
  automationId: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}) => (
  <Box
    component="button"
    type="button"
    data-automation-id={automationId}
    onClick={onClick}
    title={label === undefined ? title : undefined}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: label ? '11px' : 0,
      py: label ? '5px' : 0,
      width: label ? undefined : 30,
      height: 30,
      justifyContent: 'center',
      border: 'none',
      borderRadius: 'var(--border-radius-pill)',
      bgcolor: 'transparent',
      fontSize: 11,
      fontFamily: 'var(--font-family)',
      color: color ?? 'var(--fg-2)',
      cursor: 'pointer',
      '&:hover': { bgcolor: color ? 'var(--color-coral-10)' : 'var(--bg-hover)' },
    }}
  >
    <MaterialSymbol name={icon} size={label ? 16 : 18} />
    {label}
    {label ? <MaterialSymbol name="expand_more" size={14} color="var(--fg-3)" /> : null}
  </Box>
);

const Divider = () => <Box sx={{ width: '1px', height: 22, bgcolor: 'var(--border-divider)' }} />;

export const BulkActionBar = () => {
  const { t } = useTranslation(['pmPlanning', 'projectManagement']);
  const { state, dispatch, flat, contractInputs } = usePlanEditor();
  const [menu, setMenu] = useState<{
    kind: 'contract';
    anchor: HTMLElement;
  } | null>(null);

  if (state.selectedIds.size === 0) return null;

  const ids = state.selectedIds;
  /** Visible order, for indent/outdent sweeps. */
  const orderedIds = flat.rows.filter((row) => ids.has(row.id)).map((row) => row.id);
  const closeMenu = () => setMenu(null);

  const menuFor = (kind: 'contract'): ReactNode => {
    switch (kind) {
      case 'contract':
        return contractInputs.map((contract) => (
          <MenuItem
            key={contract.id}
            data-automation-id="bulk-contract-option"
            onClick={() => {
              dispatch({
                type: 'BULK_SET_FIELDS',
                ids,
                taskOnly: true,
                patch: { contractId: contract.id },
              });
              closeMenu();
            }}
          >
            <ContractChip type={contract.type} label={contract.name} />
          </MenuItem>
        ));
    }
  };

  return (
    <Box
      data-automation-id="bulk-bar"
      onClick={(event) => event.stopPropagation()}
      sx={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: 95,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 2,
        pr: 1,
        py: 1,
        bgcolor: 'var(--bg-panel)',
        border: 'var(--border-width-thin) solid var(--border-subtle)',
        borderRadius: 'var(--border-radius-pill)',
        boxShadow: 'var(--shadow-small)',
        animation: 'su var(--duration-fast) ease-out',
      }}
    >
      <Box
        component="span"
        data-automation-id="bulk-count"
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--color-blue-80)',
          fontFamily: 'var(--font-family-mono)',
          pr: 0.5,
        }}
      >
        {t('bulk.Selected', { count: state.selectedIds.size })}
      </Box>
      <Divider />

      {/*
        No "Status" either (Mette, 3 Nov): a draft has no status to set, and
        the plan table's Status column went the same day.
      */}
      {/*
        No "Assign" here. It wrote the legacy `assignee` field, which allocations
        supersede everywhere they exist — `TaskInsightPanel` reads the staffing
        lines first and only falls back to `assignee` — so assigning people in
        bulk looked like it worked and left the rows unstaffed. Staffing is a
        one-row job through the Resources popover, where the hours and role that
        make an allocation real can be given at the same time.
      */}
      <BarButton
        icon="receipt_long"
        label={t('bulk.Billing')}
        automationId="bulk-billing"
        onClick={(event) => setMenu({ kind: 'contract', anchor: event.currentTarget })}
      />

      <Divider />
      <BarButton
        icon="format_indent_decrease"
        title={t('bulk.Outdent')}
        automationId="bulk-outdent"
        onClick={() => dispatch({ type: 'BULK_OUTDENT', ids: orderedIds })}
      />
      <BarButton
        icon="format_indent_increase"
        title={t('bulk.Indent')}
        automationId="bulk-indent"
        onClick={() => dispatch({ type: 'BULK_INDENT', ids: orderedIds })}
      />
      <BarButton
        icon="content_copy"
        title={t('bulk.Duplicate')}
        automationId="bulk-duplicate"
        onClick={() =>
          dispatch({ type: 'DUPLICATE_NODES', ids, makeId: () => crypto.randomUUID() })
        }
      />
      <BarButton
        icon="delete"
        title={t('bulk.Delete')}
        color="var(--color-coral-80)"
        automationId="bulk-delete"
        onClick={() => dispatch({ type: 'DELETE_NODES', ids })}
      />
      <Divider />
      <BarButton
        icon="close"
        title={t('bulk.Clear')}
        automationId="bulk-clear"
        onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}
      />

      <Menu open={menu !== null} anchorEl={menu?.anchor ?? null} onClose={closeMenu}>
        {menu ? menuFor(menu.kind) : null}
      </Menu>
    </Box>
  );
};
